import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Dialog, { DialogVariant } from '../ui/Dialog';
import AdminSectionSwitch from './AdminSectionSwitch';

type RoleName = 'novagob_team' | 'jury' | 'public';

const ROLE_OPTIONS: { value: RoleName; label: string }[] = [
  { value: 'novagob_team', label: 'Equipo NovaGob' },
  { value: 'jury', label: 'Jurado' },
  { value: 'public', label: 'Público' },
];

const roleLabel = (r: string) =>
  ROLE_OPTIONS.find((o) => o.value === r)?.label || r;

interface UserWithRoles {
  user_id: string;
  email: string;
  roles: RoleName[];
}

interface ProfileSearchResult {
  id: string;
  email: string | null;
  name: string | null;
  last_name: string | null;
  username: string | null;
  roles: RoleName[];
}

const ManageRoles: React.FC = () => {
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [dialog, setDialog] = useState<{
    open: boolean;
    title?: string;
    message: string;
    variant?: DialogVariant;
    onConfirm?: () => void;
    confirmText?: string;
  }>({ open: false, message: '' });
  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const { isNovagobTeam, user: currentUser, refreshRoles } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [profileSearchTerm, setProfileSearchTerm] = useState('');
  const [profileResults, setProfileResults] = useState<ProfileSearchResult[]>([]);

  // Dropdowns "+ Añadir"
  const [addMenuFor, setAddMenuFor] = useState<string | null>(null);
  const addMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isNovagobTeam) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNovagobTeam]);

  useEffect(() => {
    if (!addMenuFor) return;
    const onDoc = (e: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
        setAddMenuFor(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [addMenuFor]);

  const groupRolesByUser = (
    rows: { user_id: string; email: string; role: string }[]
  ): UserWithRoles[] => {
    const map = new Map<string, UserWithRoles>();
    rows
      .filter((r) => r.role !== 'public')
      .forEach((r) => {
        const current = map.get(r.user_id);
        if (current) {
          if (!current.roles.includes(r.role as RoleName)) {
            current.roles.push(r.role as RoleName);
          }
        } else {
          map.set(r.user_id, {
            user_id: r.user_id,
            email: r.email,
            roles: [r.role as RoleName],
          });
        }
      });
    return Array.from(map.values()).sort((a, b) =>
      (a.email || '').localeCompare(b.email || '')
    );
  };

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Filtramos role != 'public' en el propio SQL (no en el cliente):
      // la tabla tiene ~10.000 filas 'public' y PostgREST corta cada
      // respuesta sin paginar a un límite por defecto, así que traer todo
      // y filtrar después puede dejar fuera usuarios con rol real si su
      // email no cae en las primeras filas devueltas (mismo bug ya
      // corregido en /votaciones con public_votes).
      const { data, error } = await supabase
        .schema('premios')
        .from('roles_with_emails')
        .select('*')
        .neq('role', 'public')
        .order('email');

      if (!error && data && data.length > 0) {
        setUsers(groupRolesByUser(data as any[]));
        return;
      }

      if (error) {
        console.warn(
          'No se pudo cargar roles desde roles_with_emails, fallback a user_roles:',
          error
        );
      }

      // Fallback cuando la vista no está disponible o devuelve vacío
      const { data: fallbackData, error: fallbackError } = await supabase
        .schema('premios')
        .from('user_roles')
        .select('user_id, role')
        .neq('role', 'public')
        .order('role');

      if (fallbackError) throw fallbackError;

      const userIds = (fallbackData || []).map((item: any) => item.user_id);
      let emailMap: Record<string, string> = {};

      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .schema('id')
          .from('users')
          .select('id, email')
          .in('id', userIds);

        if (!profilesError && profilesData) {
          emailMap = (profilesData as any[]).reduce((acc, profile) => {
            acc[profile.id] = profile.email || 'Unknown';
            return acc;
          }, {} as Record<string, string>);
        } else {
          console.warn(
            'No se pudo recuperar perfiles para emails en fallback:',
            profilesError
          );
        }
      }

      const fallbackRows = (fallbackData || []).map((item: any) => ({
        user_id: item.user_id,
        email: emailMap[item.user_id] || 'Unknown',
        role: item.role,
      }));

      setUsers(groupRolesByUser(fallbackRows));
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const searchProfiles = async () => {
    const term = profileSearchTerm.trim();
    setMessage('');

    if (term.length < 2) {
      setProfileResults([]);
      return;
    }

    try {
      setLoading(true);

      const { data: profilesData, error: profilesError } = await supabase
        .schema('id')
        .from('users')
        .select('id, email, name, last_name, username')
        .or(
          [
            `email.ilike.%${term}%`,
            `username.ilike.%${term}%`,
            `name.ilike.%${term}%`,
            `last_name.ilike.%${term}%`,
          ].join(',')
        )
        .limit(25);

      if (profilesError) throw profilesError;

      const profiles = (profilesData || []) as any[];
      const ids = profiles.map((p) => p.id).filter(Boolean);

      let rolesByUserId: Record<string, RoleName[]> = {};
      if (ids.length > 0) {
        const { data: rolesData, error: rolesError } = await supabase
          .schema('premios')
          .from('user_roles')
          .select('user_id, role')
          .in('user_id', ids);

        if (rolesError) throw rolesError;

        rolesByUserId = (rolesData || []).reduce(
          (acc: Record<string, RoleName[]>, r: any) => {
            acc[r.user_id] = acc[r.user_id] || [];
            acc[r.user_id].push(r.role as RoleName);
            return acc;
          },
          {}
        );
      }

      const merged: ProfileSearchResult[] = profiles.map((p) => ({
        id: p.id,
        email: p.email ?? null,
        name: p.name ?? null,
        last_name: p.last_name ?? null,
        username: p.username ?? null,
        roles: rolesByUserId[p.id] || [],
      }));

      setProfileResults(merged);
    } catch (error: any) {
      console.error('Error searching profiles:', error);
      setProfileResults([]);
      setMessage('Error buscando en perfiles: ' + (error?.message || 'desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const assignRole = async (userId: string, role: RoleName) => {
    setMessage('');
    try {
      setLoading(true);
      const { error } = await supabase
        .schema('premios')
        .from('user_roles')
        .upsert({ user_id: userId, role }, { onConflict: 'user_id,role' });

      if (error) throw error;

      setMessage('Rol asignado correctamente.');
      setAddMenuFor(null);
      await Promise.all([
        loadUsers(),
        profileResults.length ? searchProfiles() : Promise.resolve(),
        userId === currentUser?.id ? refreshRoles() : Promise.resolve(),
      ]);
    } catch (error: any) {
      console.error('Error assigning role:', error);
      setMessage('Error asignando rol: ' + (error?.message || 'desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const removeRole = (userId: string, role: RoleName) => {
    setDialog({
      open: true,
      title: 'Eliminar rol',
      message: `¿Eliminar el rol "${roleLabel(role)}" de este usuario?`,
      variant: 'danger',
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          setLoading(true);
          const { error } = await supabase
            .schema('premios')
            .from('user_roles')
            .delete()
            .eq('user_id', userId)
            .eq('role', role);
          if (error) throw error;
          await Promise.all([
            loadUsers(),
            profileResults.length ? searchProfiles() : Promise.resolve(),
            userId === currentUser?.id ? refreshRoles() : Promise.resolve(),
          ]);
        } catch (error: any) {
          console.error('Error removing role:', error);
          setDialog({
            open: true,
            title: 'Error',
            message: 'Error al eliminar el rol: ' + (error?.message || 'desconocido'),
            variant: 'error',
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const filteredUsers = users.filter((u) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      (u.email || '').toLowerCase().includes(term) ||
      u.roles.some((r) => roleLabel(r).toLowerCase().includes(term))
    );
  });

  if (!isNovagobTeam) {
    return (
      <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
        <div className="card">
          <p>No tienes permisos para acceder a esta página.</p>
        </div>
      </div>
    );
  }

  // Pill + "+ Añadir" dropdown compartido entre ambas secciones
  const renderRolesRow = (
    userId: string,
    currentRoles: RoleName[],
    keyPrefix: string
  ) => {
    const missing: RoleName[] = ROLE_OPTIONS.map((o) => o.value).filter(
      (r) => !currentRoles.includes(r)
    );
    const canAdd = missing.length > 0;
    const menuId = `${keyPrefix}-${userId}`;
    const isOpen = addMenuFor === menuId;

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {currentRoles.length === 0 && (
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Sin roles
          </span>
        )}

        {currentRoles.map((role) => (
          <span
            key={role}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 6px 5px 10px',
              borderRadius: 999,
              background: 'rgba(255, 199, 0, 0.12)',
              border: '1px solid rgba(255, 199, 0, 0.35)',
              color: '#fde68a',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1,
            }}
          >
            {roleLabel(role)}
            <button
              type="button"
              onClick={() => removeRole(userId, role)}
              disabled={loading}
              title="Quitar rol"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                color: '#fde68a',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <X size={11} />
            </button>
          </span>
        ))}

        <div style={{ position: 'relative' }} ref={isOpen ? addMenuRef : null}>
          <button
            type="button"
            onClick={() => setAddMenuFor(isOpen ? null : menuId)}
            disabled={loading || !canAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '8px 10px',
              borderRadius: 999,
              background: isOpen ? 'rgba(255, 199, 0, 0.12)' : 'transparent',
              border: `1px dashed ${
                isOpen ? 'rgba(255, 199, 0, 0.5)' : 'rgba(255,255,255,0.25)'
              }`,
              color: canAdd ? 'var(--color-text-secondary)' : 'rgba(255,255,255,0.3)',
              fontSize: 12,
              fontWeight: 500,
              cursor: canAdd ? 'pointer' : 'not-allowed',
              lineHeight: 1,
            }}
          >
            <Plus size={12} />
            {!canAdd
              ? 'Todos asignados'
              : currentRoles.length > 0
              ? 'Añadir'
              : 'Asignar rol'}
          </button>

          {isOpen && canAdd && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                zIndex: 50,
                minWidth: 220,
                display: 'flex',
                flexDirection: 'column',
                background: '#003554',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 10,
                boxShadow: '0 12px 30px rgba(0,0,0,0.4)',
                padding: 8,
              }}
            >
              {missing.map((role) => (
                <button
                  key={role}
                  type="button"
                  disabled={loading}
                  onClick={() => assignRole(userId, role)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    borderRadius: 6,
                    background: 'transparent',
                    border: 'none',
                    color: '#fff',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = 'rgba(255, 199, 0, 0.12)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'transparent')
                  }
                >
                  {roleLabel(role)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container dashboard">
      <div
        className="dashboard-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}
      >
        <div>
          <h1>Gestión de Roles</h1>
          <p>Panel de Administración</p>
        </div>
        <AdminSectionSwitch />
      </div>

      <div className="admin-main">
        <div className="card" style={{ marginBottom: '24px' }}>
          <h2 style={{ marginTop: 0 }}>Dar de alta usuario en Premios</h2>
          <p style={{ color: 'var(--color-text-secondary)', marginTop: '8px' }}>
            Si un usuario ya está registrado en la red NovaGob, puedes buscarlo aquí y asignarle uno o varios roles.
          </p>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              marginTop: '16px',
              flexWrap: 'wrap',
            }}
          >
            <div className="form-group" style={{ margin: 0, flex: '1 1 320px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Buscar por email, username, nombre o apellidos..."
                value={profileSearchTerm}
                onChange={(e) => setProfileSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') searchProfiles();
                }}
              />
            </div>
            <button
              className="btn btn-secondary"
              onClick={searchProfiles}
              disabled={loading}
            >
              Buscar
            </button>
          </div>

          {message && (
            <div
              className={`alert ${
                message.toLowerCase().startsWith('error') ? 'alert-error' : 'alert-success'
              }`}
              style={{ marginTop: '16px' }}
            >
              {message}
            </div>
          )}

          {profileResults.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              {profileResults.map((p) => {
                const displayName = [p.name, p.last_name].filter(Boolean).join(' ').trim();
                const initials = (p.email || p.username || '?')
                  .split('@')[0]
                  .slice(0, 2)
                  .toUpperCase();

                return (
                  <div
                    key={p.id}
                    style={{
                      padding: 14,
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          background: 'rgba(255, 199, 0, 0.15)',
                          color: 'var(--color-secondary)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p.email || 'Sin email'}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {displayName || 'Sin nombre'}
                          {p.username ? ` · @${p.username}` : ''}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          color: 'var(--color-text-secondary)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.roles.length} rol{p.roles.length === 1 ? '' : 'es'}
                      </span>
                    </div>

                    {renderRolesRow(p.id, p.roles, 'search')}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              marginBottom: '18px',
            }}
          >
            <div>
              <h2 style={{ margin: 0, marginBottom: 4 }}>Usuarios y Roles</h2>
              <p
                style={{
                  margin: 0,
                  color: 'var(--color-text-secondary)',
                  fontSize: 13,
                }}
              >
                {users.length} usuario{users.length === 1 ? '' : 's'} con rol asignado
              </p>
            </div>

            <div style={{ position: 'relative', width: '100%', maxWidth: 300 }}>
              <Search
                size={15}
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--color-text-secondary)',
                }}
              />
              <input
                type="text"
                className="form-input"
                placeholder="Buscar por email o rol..."
                style={{ paddingLeft: 34 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredUsers.map((u) => {
              const initials = (u.email || '?')
                .split('@')[0]
                .slice(0, 2)
                .toUpperCase();
              return (
                <div
                  key={u.user_id}
                  style={{
                    padding: 14,
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        background: 'rgba(255, 199, 0, 0.15)',
                        color: 'var(--color-secondary)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {u.email || 'Sin email en id.users (revisar perfil)'}
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--color-text-secondary)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {u.roles.length} rol{u.roles.length === 1 ? '' : 'es'}
                    </span>
                  </div>

                  {renderRolesRow(u.user_id, u.roles, 'list')}
                </div>
              );
            })}
          </div>

          {filteredUsers.length === 0 && (
            <div
              style={{
                padding: '40px',
                textAlign: 'center',
                color: 'var(--color-text-secondary)',
              }}
            >
              No se encontraron usuarios con ese criterio.
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
      <Dialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
        confirmText={dialog.confirmText}
        onConfirm={dialog.onConfirm}
        onClose={closeDialog}
      />
    </div>
  );
};

export default ManageRoles;
